<template>
<!-- <link rel="amphtml" href="https://tion.ru/blog/amp/"> -->

  <div class="container tes-2column-layout">

    <div class="tes-2column-layout__navigation">

      <TagsDropDown :tid="tid">
        <template #selectButton="{title}">
          <h3
            class="tes-tags-dropdown__button"><span class="tes-tags-dropdown__button-text">#{{ title }}</span></h3>
        </template>
      </TagsDropDown>

      <PostsSearchForm/>

    </div>

    <section class="tes-2column-layout__content tes-post">

      <BreadcrumbsView :pagination="null" :items="breadcrumbs"/>

      <PostEmptyView v-if="!post.id()"/>
      <PostView :post="post" v-else/>


      <div class="tes-article__social-panel">

        <ObserverComponent>
        <div class="tes-article__ratings-container">
            <span class="tes-article__ratings-header">Рейтинг статьи</span>
            <PostRating :item="post" ownerType="post" :k="String('post_' + post.id())"/>
        </div>

        <div class="tes-article__socials-share-container">
            <span class="tes-article__socials-share-header">Поделиться</span>
            <PostSocialShare :nets="['vk']" :item="post"/>
        </div>
        </ObserverComponent>

      </div>


      <section class="tes-article__subscription-container" id="subscription_to_articles">
        <ObserverComponent>
          <MailSubscriptionBlogForm v-once/>
        </ObserverComponent>
      </section>

      <ObserverComponent>
        <Suspense>
          <template #default>
            <PostReadAlsoListView v-once/>
          </template>
          <template #fallback>
            <EllipsisLoader :loading="true" class="tes-loader__overlay_inner"/>
          </template>
        </Suspense>
      </ObserverComponent>

      <Suspense>
        <template #default>
          <CommentListView ownerType="post" :initialCount="post.countComments()" :ownerId="post.id()"/>
        </template>
        <template #fallback>
          <EllipsisLoader :loading="true" class="tes-loader__overlay_inner"/>
        </template>
      </Suspense>

    </section>

    <aside class="tes-2column-layout__aside">

      <ObserverComponent>
        <Suspense>
          <template #default>
            <PostAsideListView v-once/>
          </template>
          <template #fallback>
            <EllipsisLoader :loading="true" class="tes-loader__overlay_inner"/>
          </template>
        </Suspense>
      </ObserverComponent>

      <ObserverComponent>
        <Suspense>
          <template #default>
            <BannerAsideListView v-once/>
          </template>
          <template #fallback>
            <EllipsisLoader :loading="true" class="tes-loader__overlay_inner"/>
          </template>
        </Suspense>
      </ObserverComponent>

    </aside>

  </div>

</template>

<script lang="ts">
import Post, {PostData, PostInterface, postEmptyData} from '../../objects/post/Post'
import {BreadcrumbData, Breadcrumbs} from '../../objects/Breadcrumb'
import {Loader, loaderStateSymbol} from '../../states/loader'
import {
  pageHeadersStateSymbol,
  PageHeadsStore,
  prepareHead
} from '../../states/page-heads'
import {useStore} from 'vuex'
import {defineAsyncComponent, inject, onMounted, onServerPrefetch, Ref, ref, watch} from 'vue'
import {useRouter} from 'vue-router'

import BreadcrumbsView from '../../components/BreadcrumbsView.vue'
import MailSubscriptionBlogForm from '../../components/form/MailSubscriptionBlogForm.vue'
import PostRating from '../../components/post/PostRating.vue'
import PostSocialShare from '../../components/post/PostSocialShare.vue'
import PostsSearchForm from '../../components/post/PostsSearchForm.vue'
import PostEmptyView from '../../components/post/PostEmptyView.vue'
import PostView from '../../components/post/PostView.vue'
import TagsDropDown from '../../components/post/TagsDropDown.vue'


export default {
  name: 'PagePost',
  components: {
    TagsDropDown,
    PostsSearchForm,
    BreadcrumbsView,
    PostEmptyView,
    PostView,
    PostAsideListView: defineAsyncComponent({
      loader: () => import('../../components/post/PostAsideListView.vue'),
      delay: 350,
      timeout: 1000
    }),
    PostRating,
    PostSocialShare,
    MailSubscriptionBlogForm,
    CommentListView: defineAsyncComponent({
      loader: () => import('../../components/comment/CommentListView.vue'),
      delay: 500,
      timeout: 1000
    }),
    PostReadAlsoListView: defineAsyncComponent({
      loader: () => import('../../components/post/PostReadAlsoListView.vue'),
      delay: 650,
      timeout: 1000
    }),
    BannerAsideListView: defineAsyncComponent({
      loader: () => import('~/components/banner/BannerAsideListView.vue'),
      delay: 1000,
      timeout: 1000
    }),
  },
  props: {
    tid: {
      type: [String, Number],
      required: true
    }
  },
  async setup(props) {

    const store = useStore()
    const router = useRouter()
    const post: Ref<PostInterface> = ref(new Post(postEmptyData))
    const loading: Loader | undefined = inject(loaderStateSymbol)
    const head: PageHeadsStore | undefined = inject(pageHeadersStateSymbol)

    const breadcrumbs = ref(new Breadcrumbs([
      {title: 'Главная', url: '/', here: false} as BreadcrumbData,
      {title: 'Блог', url: {name: 'Blog'}, here: false} as BreadcrumbData,
    ]))

    const load = () => {
      loading?.start()
      post.value = new Post(postEmptyData)

      return store.dispatch('posts/loadDataForPost',{tid: props.tid, options: null}, { root: true })
        .then((data) => {
          try {
            post.value = new Post(data as PostData)
            if (head) {
              head.updateHead(prepareHead(post.value.heads()))
            }
          } catch (e) {
            console.log(e)
            router.push({name: 'ServerError', params: {number: 3}});
          }
        })
        .catch(error => {
          console.log(error)
          if (error.status === 404) {
            router.push({name: 'NotFound'});
          } else if (error.status >= 500) {
            router.push({name: 'ServerError', params: {number: (error.status - 500)}});
          } else {
            router.push({name: 'ServerError', params: {number: 3}});
          }
        })
        .finally(() => loading?.stop())
    }

    watch(() => props.tid, () => {
      load()
    })

    onMounted(() => {
      load()
    })

    onServerPrefetch(() => {
      return load()
    })

    return {
      post, breadcrumbs
    }
  },
}
</script>

<style src="~/styles/post-page.scss"/>
